import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  AnimatedDiv,
  ShowAnimation,
} from '@vimily/bonjoro-ui-components-v2/AnimatedDiv';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useHistory, useRouteMatch } from 'react-router';
import { Title } from '../Components/Common/Title';
import { useQueryString } from '~/react-hooks/use-query-string';
import { PlanCard, PlasmicModelType } from '~/types/models/plasmic';
import { usePlasmicCMS } from '~/App/Api/Plasmic';
import { useSubscriptions } from '../Hooks/useSubscription';
import { useCustomerCoupon } from '~/App/Api/Coupon';
import { DiscountPill } from '../Components/Common/DiscountPill';
import { BillingCycle, CheckoutPlan, Service } from '~/types/models';
import {
  CreditCardWithIntentId,
  UpdateCreditCardArgs,
  useCreditCard,
  useUpdateCreditCard,
} from '../Hooks/useCreditCard';
import { CreditCardPanel } from '../Components/Summary/CreditCardPanel';
import { CreditCardForm } from '../Components/CreditCardForm';
import useStripe from '~/react-hooks/use-stripe';
import { Elements } from '@stripe/react-stripe-js';
import { ScaMandatePanel } from '../Components/Common/ScaMandatePanel';
import {
  getPlanCardViaService,
  useMappedPlanCards,
} from '../Hooks/useMappedPlanCards';
import { PricingPlanCards } from '../Components/Pricing/PricingPlanCards';
import {
  PricingPlanCardLayout,
  PricingPlanCardProps,
} from '../Components/Pricing/PricingPlanCards/PricingPlanCard';
import cx from 'classnames';
import { billingRoutes } from '..';
import useHideNavbar from '~/react-hooks/use-hide-navbar';
import {
  proceedCheckoutPlan,
  useCheckoutAddOn,
  useCheckoutPlans,
  useProceedCheckoutAddOn,
} from '../Hooks/useCheckout';
import { Subtitle } from '../Components/Common/Subtitle';
import FlashContext from '~/App/Components/Flash/Context';
import { AxiosError } from 'axios';
import { ActionPanel } from '../Components/Common/ActionPanel';
import { AddOnType } from '~/types/models/addon';
import { useFeatures } from '~/react-hooks/use-features';
import {
  AddOnDescriptions,
  AddOnIcons,
  AddOnLabels,
  getAddOnBillingInfo,
  getFormattedPrice,
  getPlanBillingInfo,
} from '../utils';
import {
  AddOnCard,
  AddOnCardProps,
} from '../Components/Subscription/AvailableAddOns/AddOnCard';
import { LoadingBicycle } from '~/App/Components/Loaders/LoadingBicycle';
import useCurrentTeam from '~/react-hooks/use-current-team';
import { sequentialMap } from '~/utils/misc';
import { UpgradeModalViewContext } from '~/App/Services/BillingAnalytics';
import useAnalytics from '~/react-hooks/use-analytics';
import { CHECKOUT_COMPLETED } from '~/generated/AnalyticsTypes';
import { useAddOnsPrices } from '../Hooks/useAddOnsPrice';

export enum CheckoutType {
  Plans = 'plans',
  AddOns = 'add-ons',
}

// Items could be a plan, an array of plans, or an add on
export type CheckoutItems = string[] | string | AddOnType;

export interface CheckoutQueryString {
  items: CheckoutItems;
  redirect?: string;
  context?: UpgradeModalViewContext;
}

export function Checkout() {
  const plandalf = window.plandalf;
  const history = useHistory();
  const analytics = useAnalytics();
  const stripe = useStripe();
  const { canSetupLandingPageDomain } = useFeatures();
  const { card_on_file, billing_cycle } = useCurrentTeam();

  const match = useRouteMatch<{ type?: CheckoutType }>();
  const { type } = match.params;

  const [updateCardClicked, setUpdateCardClicked] = useState(false);
  const [isCheckoutPlanSubmitting, setIsCheckoutPlanSubmitting] =
    useState(false);
  const { data: subscriptions, isLoading: isSubscriptionLoading } =
    useSubscriptions({});
  const { data: coupon } = useCustomerCoupon({});
  const {
    items,
    redirect,
    context = UpgradeModalViewContext.BillingPage,
  } = useQueryString<CheckoutQueryString>();
  const { createFlash } = useContext(FlashContext);
  const { data: creditCard, isLoading: isCreditCardLoading } = useCreditCard(
    {}
  );
  const { data: addOnPrices } = useAddOnsPrices({});
  const { on_legacy_plan: legacyTeam } = useCurrentTeam();
  const discountFormatted = subscriptions?.[0].discount_formatted; //all subscriptions has the same discounts

  const isPlansCheckoutType = type === CheckoutType.Plans;
  const isAddOnsCheckoutType = type === CheckoutType.AddOns;
  const hasItems = items != undefined;

  useEffect(() => {
    if (
      isAddOnsCheckoutType &&
      items === AddOnType.CustomDomain &&
      canSetupLandingPageDomain
    ) {
      history.push('/billing');
    }
  }, [isAddOnsCheckoutType, items]);

  const showErrorFlash = (message: string) =>
    createFlash({
      id: 'update-credit-card-error',
      title: 'Error',
      message: 'Please try again: ' + message,
      options: { style: 'danger' },
    });

  const showProcessingFlash = () =>
    createFlash({
      id: 'processing-submission',
      title: 'Processing',
      message: 'Please wait...',
    });

  const onCheckoutSuccess = () => {
    analytics.track(CHECKOUT_COMPLETED, { context });

    if (typeof items === 'string') {
      plandalf.intel?.action('complete', {
        result: 'transaction_successful',
        id: items,
        element: 'checkout-page',
        flow: 'upgrade-button',
        scenario: `Checkout ${
          type === CheckoutType.Plans ? 'standard plan' : 'add-ons'
        } in checkout page.`,
      });
    }

    // Force reload to get the updated bonjoro global variables
    if (redirect) {
      window.location.href = redirect;
    } else {
      window.location.href = billingRoutes.summary;
    }
  };

  const {
    mutateAsync: proceedCheckoutAddOn,
    isLoading: isProceedingCheckoutAddOn,
  } = useProceedCheckoutAddOn({
    options: {
      onSuccess: () => {
        createFlash({
          id: 'checkout-add-on-success',
          title: 'Success!',
          message: 'Add-on checkout processed successfully! Redirecting...',
        });

        onCheckoutSuccess();
      },
    },
  });

  const proceedCheckoutPlanAction = useCallback(
    (intentId?: string) => {
      if (!items) return;

      setIsCheckoutPlanSubmitting(true);
      const planSlugs = Array.isArray(items) ? items : [items];

      /**
       * Run the API sequentially. This is to avoid running the API in parallel and
       * having a race condition that causes the other API to fail.
       */
      sequentialMap(
        planSlugs.map(
          (planSlug) => () =>
            proceedCheckoutPlan({
              intentId: intentId,
              planSlug,
              legacyTeam,
            })
        )
      ).then(() => {
        createFlash({
          id: 'checkout-plan-success',
          title: 'Success!',
          message: 'Plan checkout processed successfully! Redirecting...',
        });

        onCheckoutSuccess();
      });
    },
    [items, legacyTeam]
  );

  const proceedCheckoutAddOnAction = useCallback(
    (paymentMethodId?: string) =>
      proceedCheckoutAddOn({
        paymentMethodId,
        addOn: items as AddOnType,
      }),
    [items]
  );

  const onCheckoutAction = ({
    paymentMethodId,
    intentId,
  }: {
    paymentMethodId?: string;
    intentId?: string;
  }) => {
    if (isPlansCheckoutType) {
      void proceedCheckoutPlanAction(intentId);
    } else if (isAddOnsCheckoutType) {
      void proceedCheckoutAddOnAction(paymentMethodId);
    }
  };

  const { mutateAsync: updateCreditCard, isLoading: isUpdatingCreditCard } =
    useUpdateCreditCard({
      options: {
        onSuccess: (result) => {
          if ('error' in result) {
            showErrorFlash(result?.error?.message ?? 'Something went wrong...');
          } else {
            const creditCard = result as CreditCardWithIntentId;

            onCheckoutAction({
              paymentMethodId: creditCard.payment_method_id,
              intentId: creditCard.intentId,
            });
          }
        },
        onError: (error: AxiosError) => {
          showErrorFlash(error?.message);
        },
      },
    });

  const checkoutPlansQuery = useCheckoutPlans({
    plans: Array.isArray(items) ? items : ([items] as string[]),
    options: {
      enabled: isPlansCheckoutType && hasItems,
    },
  });

  const checkoutPlans = useMemo(() => {
    return checkoutPlansQuery.map((planQuery) => planQuery.data);
  }, [checkoutPlansQuery]);

  useEffect(() => {
    if (typeof items === 'string') {
      plandalf.intel?.action('start', {
        items: [{ type: 'plan', id: items }],
        flow: 'upgrade-button',
      });
    }
  }, []);

  const isCheckoutPlansLoading = useMemo(() => {
    return (
      typeof checkoutPlansQuery.find((planQuery) => planQuery.isLoading) !==
      'undefined'
    );
  }, [checkoutPlansQuery]);

  const { data: checkoutAddOn, isLoading: isCheckoutAddOnLoading } =
    useCheckoutAddOn({
      addOn: items as AddOnType,
      options: {
        enabled: isAddOnsCheckoutType && hasItems,
      },
    });

  useHideNavbar();

  const { data: planCards } = usePlasmicCMS<PlanCard>({
    model: PlasmicModelType.PlanCard,
    query: {
      category: 'pricing',
    },
    options: {
      enabled: isPlansCheckoutType && hasItems,
    },
  });

  const mappedPlanCards = useMappedPlanCards({
    plasmicPlanCards: planCards?.filter(
      (planCards) =>
        items?.includes(planCards?.details?.monthlyPlanSlug) ||
        items?.includes(planCards?.details?.annualPlanSlug)
    ),
    coupon: coupon?.coupon,
    subscriptions,
    billingCycle: checkoutPlans[0]?.billing_cycle,
  });

  const vmPlanCard = getPlanCardViaService({
    service: Service.VideoMessaging,
    planCards: mappedPlanCards,
  });

  const tmPlanCard = getPlanCardViaService({
    service: Service.Testimonials,
    planCards: mappedPlanCards,
  });

  const onBackClick = () => {
    if (redirect) {
      window.location.href = redirect;
    } else {
      history.goBack();
    }
  };

  const onCreditCardBackClick = () => {
    if (updateCardClicked) {
      setUpdateCardClicked(false);
    } else {
      onBackClick?.();
    }
  };

  const {
    titleLabel,
    billingDescription,
    sortedPlanCards,
    discountPillService,
    addOnCardProps,
  } = useMemo(() => {
    let label = '';
    let billingDescription: string | undefined = undefined;
    let pillService: Service | undefined = undefined;
    let addOnCardProps: AddOnCardProps | undefined = undefined;
    const planCards: PricingPlanCardProps[] = [];

    if (isPlansCheckoutType && !isCheckoutPlansLoading) {
      billingDescription = getPlanBillingInfo(checkoutPlans as CheckoutPlan[]);

      if (vmPlanCard) {
        label = `${label} ${vmPlanCard?.title?.label ?? ''}`;
        planCards.push(vmPlanCard);
        pillService = Service.VideoMessaging;
      }

      if (vmPlanCard && tmPlanCard) {
        label = `${label} and`;
        pillService = undefined;
      }

      if (tmPlanCard) {
        label = `${label} ${tmPlanCard?.title?.label ?? ''}`;
        planCards.push(tmPlanCard);

        if (!vmPlanCard) {
          pillService = Service.Testimonials;
        }
      }
    } else if (
      isAddOnsCheckoutType &&
      !isCheckoutAddOnLoading &&
      !isSubscriptionLoading
    ) {
      const addOnType = items as AddOnType;
      label = `${label} ${AddOnLabels[addOnType]} add-on`;
      billingDescription = getAddOnBillingInfo({
        addOnDetails: checkoutAddOn!,
        subscriptions,
      });

      const addOnPrice = addOnPrices?.find((item) => item.type === addOnType);
      const monthlyPriceItem = addOnPrice?.addons.find(
        (item) => item.interval === BillingCycle.Monthly && item.is_active
      );
      const yearlyPriceItem = addOnPrice?.addons.find(
        (item) => item.interval === BillingCycle.Yearly && item.is_active
      );

      addOnCardProps = {
        className: 'lg:w-[353px]',
        imgUrl: AddOnIcons[addOnType][pillService ?? Service.VideoMessaging],
        title: AddOnLabels[addOnType],
        description:
          AddOnDescriptions[addOnType][pillService ?? Service.VideoMessaging],
        billingPrice: `$${monthlyPriceItem?.amount as number}`,
        billingCycle: '/mo',
        billingDescription:
          billing_cycle === BillingCycle.Yearly
            ? `Billed $${getFormattedPrice(
                yearlyPriceItem?.amount as unknown as number
              )} USD annually`
            : undefined,
      };
    }

    return {
      billingDescription,
      addOnCardProps,
      titleLabel: label,
      sortedPlanCards: planCards,
      discountPillService: pillService,
    };
  }, [
    isPlansCheckoutType,
    isAddOnsCheckoutType,
    vmPlanCard,
    tmPlanCard,
    checkoutAddOn,
    checkoutPlans,
    subscriptions,
    isCheckoutPlansLoading,
    isCheckoutAddOnLoading,
    isSubscriptionLoading,
  ]);

  const showForm =
    creditCard == undefined || (creditCard != undefined && updateCardClicked);

  const onePlanCard = sortedPlanCards.length === 1;
  const twoPlanCards = sortedPlanCards.length === 2;

  const showDiscountPill = coupon?.coupon && subscriptions?.[0].discount;

  const isSubmitting =
    isUpdatingCreditCard ||
    isCheckoutPlanSubmitting ||
    isProceedingCheckoutAddOn;
  const isCheckoutDetailsLoading =
    isCheckoutAddOnLoading || isCheckoutPlansLoading;

  const ctaLabel = 'Confirm payment';
  const cancelLabel = 'Cancel';

  const onCreditCardSubmitClick = (formData: UpdateCreditCardArgs) => {
    showProcessingFlash();
    void updateCreditCard(formData);
  };

  const onCheckoutConfirmClick = () => {
    showProcessingFlash();
    onCheckoutAction({});
  };

  if (!sortedPlanCards || isCreditCardLoading || isCheckoutDetailsLoading) {
    return (
      <div className='w-full h-full flex items-center justify-center'>
        <LoadingBicycle />
      </div>
    );
  }

  return (
    <Elements stripe={stripe}>
      <AnimatedDiv
        show
        showAnimation={ShowAnimation.FadeIn150}
        className='flex flex-col gap-y-7 max-w-[1200px]'
      >
        <div className='flex flex-row flex-wrap justify-start items-center gap-x-4 gap-y-4'>
          <FontAwesomeIcon
            icon={faChevronLeft}
            className='text-[35px] cursor-pointer v2-text-neutral-900 hover:v2-text-yellow-500'
            onClick={onBackClick}
          />
          <Title label={`Payment for ${titleLabel}`} />
          {showDiscountPill && discountFormatted && (
            <DiscountPill
              className='text-base !h-[30px] leading-5'
              label={`${discountFormatted} off applied`}
              service={discountPillService}
            />
          )}
        </div>
        {isAddOnsCheckoutType && addOnCardProps && (
          <AnimatedDiv show className='w-full'>
            <AddOnCard {...addOnCardProps} />
          </AnimatedDiv>
        )}
        <AnimatedDiv show={!isCheckoutDetailsLoading} className='w-full'>
          <Subtitle label={billingDescription} />
        </AnimatedDiv>
        <div
          className={cx('flex gap-x-4 gap-y-4 items-start w-full', {
            'flex-col min-[530px]:flex-row': isPlansCheckoutType && onePlanCard,
            'flex-col min-[1080px]:flex-row':
              isPlansCheckoutType && twoPlanCards,
            'flex-col': isAddOnsCheckoutType,
          })}
        >
          {isPlansCheckoutType && (
            <PricingPlanCards
              pricingPlanCards={sortedPlanCards}
              discounted={coupon?.coupon != undefined}
              layout={PricingPlanCardLayout.Portrait}
              billingCycle={checkoutPlans[0]?.billing_cycle}
              hideCta
              hideBadge
            />
          )}
          <div className='flex flex-col gap-y-6 w-full '>
            {showForm && (
              <AnimatedDiv show className='w-full'>
                <CreditCardForm
                  ctaClassName='confirm-payment'
                  ctaLabel={ctaLabel}
                  cancelLabel={cancelLabel}
                  onCancel={onCreditCardBackClick}
                  onSubmit={onCreditCardSubmitClick}
                  isSubmitting={isSubmitting}
                />
              </AnimatedDiv>
            )}
            {!showForm && (
              <AnimatedDiv show className='w-full flex flex-col gap-y-4'>
                <CreditCardPanel
                  hasSavedCreditCard={card_on_file}
                  creditCard={creditCard}
                  boxClassName='flex-wrap'
                  buttonContainerClassName='w-full min-[530px]:w-auto'
                  detailContainerClassName='flex-wrap'
                  onClick={() => setUpdateCardClicked(true)}
                />
                <ScaMandatePanel />
                <ActionPanel
                  ctaClassName='confirm-payment'
                  onCtaClick={onCheckoutConfirmClick}
                  onCancelClick={onBackClick}
                  ctaLabel={ctaLabel}
                  cancelLabel={cancelLabel}
                  loading={isSubmitting}
                  disabled={isSubmitting}
                />
              </AnimatedDiv>
            )}
          </div>
        </div>
      </AnimatedDiv>
    </Elements>
  );
}
